import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import TitanLogo from "@/components/TitanLogo";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/fleet", label: "Fleet" },
  { to: "/services", label: "Services" },
  { to: "/quote", label: "Quote" },
  { to: "/faq", label: "FAQ" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const loc = useLocation();

  return (
    <>
      {/* Top bar */}
      <div className="brand-strip text-white text-xs" data-testid="top-brand-bar">
        <div className="container-titan flex items-center justify-between py-2 gap-4">
          <div className="hidden md:flex items-center gap-5">
            <a href="tel:+233540124247" className="flex items-center gap-1.5 hover:text-white/80" data-testid="top-phone-1">
              <Phone size={12} /> 054 012 4247
            </a>
            <a href="tel:+233201234999" className="flex items-center gap-1.5 hover:text-white/80" data-testid="top-phone-2">
              <Phone size={12} /> 020 123 4999
            </a>
            <a href="https://wa.me/233244945582" target="_blank" rel="noreferrer noopener" className="flex items-center gap-1.5 hover:text-white/80" data-testid="top-whatsapp">
              <MessageCircle size={12} /> Click for WhatsApp Chat
            </a>
          </div>
          <div className="uppercase tracking-widest font-semibold opacity-90 text-center flex-1 md:text-right">
            3 Maseru St. East Legon, Accra · Ghana
          </div>
        </div>
      </div>

      <header
        className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-gray-200"
        data-testid="main-header"
      >
        <div className="container-titan flex items-center justify-between py-3">
          <Link to="/" data-testid="header-logo-link">
            <TitanLogo variant="dark" size="md" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="primary-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `nav-link text-sm font-semibold tracking-wide uppercase ${isActive ? "active text-[#e63946]" : "text-[#0d2040]"}`
                }
                data-testid={`nav-link-${n.label.toLowerCase()}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {user && user.role === "admin" ? (
              <Link to="/admin" className="btn-outline-navy" data-testid="header-admin-link">
                Admin
              </Link>
            ) : (
              <Link to="/admin/login" className="btn-outline-navy" data-testid="header-admin-login-link">
                Admin
              </Link>
            )}
            <Link to="/fleet" className="btn-primary" data-testid="header-book-now-btn">
              Book a Car
            </Link>
          </div>

          <button
            className="lg:hidden p-2 text-[#0d2040]"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-gray-200 bg-white" data-testid="mobile-nav">
            <div className="container-titan flex flex-col gap-1 py-4">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `py-2.5 text-sm font-semibold uppercase tracking-wider ${isActive ? "text-[#e63946]" : "text-[#0d2040]"}`
                  }
                  data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/fleet"
                onClick={() => setOpen(false)}
                className="btn-primary mt-3 w-full"
                data-testid="mobile-book-now-btn"
              >
                Book a Car
              </Link>
              {user && user.role === "admin" ? (
                <Link to="/admin" onClick={() => setOpen(false)} className="btn-outline-navy mt-2 w-full">
                  Admin
                </Link>
              ) : (
                <Link to="/admin/login" onClick={() => setOpen(false)} className="btn-outline-navy mt-2 w-full">
                  Admin Login
                </Link>
              )}
            </div>
          </div>
        )}
      </header>
      {/* spacer left intentionally blank; sticky handles */}
      <span data-current-path={loc.pathname} className="hidden" />
    </>
  );
}
